/**
 * Step-up MFA re-verification dialog. Shown when a sensitive endpoint
 * answers 403 `mfa_step_up_required`. A fresh OTP refreshes the 5-minute
 * server-side window, then `onVerified` retries the original action.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

export const MfaStepUpDialog = ({ open, onOpenChange, onVerified }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/mfa/step-up', { otp: otp.trim() });
      setOtp('');
      onOpenChange(false);
      onVerified();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code did not verify');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="mfa-stepup-dialog">
        <DialogHeader>
          <DialogTitle>Verify it&apos;s you</DialogTitle>
          <DialogDescription>
            This action is sensitive. Enter a fresh 6-digit code from your authenticator app.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="stepup-otp">One-time code</Label>
            <Input
              id="stepup-otp"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              placeholder="123456"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
              className="font-mono tracking-widest text-center text-lg"
              required
              autoFocus
              data-testid="mfa-stepup-otp-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading || otp.length < 6} data-testid="mfa-stepup-submit-btn">
              Verify
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MfaStepUpDialog;
